/**
 * BankTransactionsTable Component
 * Sortable table for bank transactions with type indicators.
 * Used in the Unreconciled drill-down modal.
 */

import { useState, useMemo } from 'react';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableHeader } from './SortableHeader';
import type { BankTransaction } from '@/types/invoices';

interface BankTransactionsTableProps {
  transactions: BankTransaction[];
}

export function BankTransactionsTable({ transactions }: BankTransactionsTableProps) {
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');

  const filtered = useMemo(() => {
    let result = transactions;
    if (typeFilter === 'income') {
      result = result.filter((tx) => tx.type.startsWith('RECEIVE'));
    } else if (typeFilter === 'expense') {
      result = result.filter((tx) => !tx.type.startsWith('RECEIVE'));
    }
    if (searchText.trim()) {
      const term = searchText.toLowerCase();
      result = result.filter((tx) =>
        (tx.contact?.name ?? '').toLowerCase().includes(term) ||
        (tx.reference ?? '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [transactions, typeFilter, searchText]);

  const keyExtractors = useMemo(() => ({
    contactName: (tx: BankTransaction) => tx.contact?.name ?? '',
  }), []);

  const { sorted, sortState, toggleSort } = useTableSort(filtered, undefined, keyExtractors);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search contact or reference..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          aria-label="Search transactions"
        />
        {(['all', 'income', 'expense'] as const).map((type) => (
          <button
            key={type}
            onClick={() => setTypeFilter(type)}
            style={{
              padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
              fontSize: '12px', cursor: 'pointer',
              background: typeFilter === type ? 'var(--marine, #075156)' : 'white',
              color: typeFilter === type ? 'white' : '#374151',
            }}
            type="button"
          >
            {type === 'all' ? 'All' : type === 'income' ? 'Income' : 'Expense'}
          </button>
        ))}
        {filtered.length !== transactions.length && (
          <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center' }}>
            {filtered.length} of {transactions.length}
          </span>
        )}
      </div>
      <table className="invoice-modal__table">
        <thead>
          <tr>
            <SortableHeader label="Date" sortKey="date" sortState={sortState} onToggle={toggleSort} />
            <th>Type</th>
            <SortableHeader label="Contact" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Reference" sortKey="reference" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Amount" sortKey="total" sortState={sortState} onToggle={toggleSort} alignRight />
          </tr>
        </thead>
        <tbody>
          {sorted.map((tx) => (
            <tr key={tx.bankTransactionID}>
              <td>{formatDate(tx.date, 'dd MMM yyyy')}</td>
              <td>
                <span className={`invoice-modal__status ${tx.type.startsWith('RECEIVE') ? 'invoice-modal__status--paid' : 'invoice-modal__status--voided'}`}>
                  {tx.type.startsWith('RECEIVE') ? 'Income' : 'Expense'}
                </span>
              </td>
              <td>{tx.contact?.name ?? '-'}</td>
              <td>{tx.reference ?? '-'}</td>
              <td className="invoice-modal__amount">{formatCurrency(tx.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
